import React, { useEffect, useId, useState } from "react";
import { Card, FieldRow, UnitInput } from "./GanttCard";
import { useSettings } from "@/hooks/useSettings";
import { localiseNote, type Quantity } from "@/lib/units";
import {
  densUnit, flowNum, flowUnit, fmtFlow, fmtKgAuto, goalInputText, goalUnit, lenText, lenUnit,
  localiseGanttText, massNum, massUnit, tNum1, tUnit,
} from "./ganttUnits";
import { ExportReportCard } from "@/components/jackstone/ExportReportCard";
import {
  n, fmt, ganttModelCode, LINKED_TO_CALC, OPT_OBJECTIVES, COST_GOAL_ENABLED, COST_WEIGHT_FIELDS,
} from "./ganttTypes";
import { hasOverride, stripOverride } from "@/lib/autocorrect";
import {
  GANTT_LIMITS, OPT_LIMITS, clampHard, inputBounds,
  DEFAULT_FLOW_MARGIN_PCT, DEFAULT_SAFETY_MARGIN_PCT,
} from "@/lib/limits";
import type {
  GanttInputs, FullResult, OptimizeResult, OptimizeOption, OptObjective, CostWeightOverrides,
} from "./ganttTypes";

interface LeftRailProps {
  inputs: GanttInputs;
  set: (key: keyof GanttInputs) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  setValue: (key: keyof GanttInputs) => (value: string) => void;   // same as set, with the SI string
  setVal: (key: keyof GanttInputs) => (value: string) => void;
  setCheck: (key: keyof GanttInputs) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  blur: (key: keyof GanttInputs) => () => void;
  notes: Partial<Record<keyof GanttInputs, string>>;   // autocorrect notes from the last blur
  manual: Record<"freezeOverride" | "defrostOverride", boolean>;
  setManual: (key: "freezeOverride" | "defrostOverride", on: boolean, prefill?: string) => void;
  clearOverride: (key: keyof GanttInputs) => void;
  errors: Partial<Record<keyof GanttInputs, string>>;
  stale: boolean;                           // results no longer match the inputs
  result: FullResult | null;
  autoFreeze: number | null;
  autoDefrost: number | null;
  effFreeze: number | null;                 // null until the AUTO value resolves
  effDefrost: number | null;
  flowMargin: string;
  setFlowMargin: (v: string) => void;
  freezeMargin: string;
  setFreezeMargin: (v: string) => void;
  maxCrews: string;
  setMaxCrews: (v: string) => void;
  costWeights: CostWeightOverrides;
  setCostWeights: React.Dispatch<React.SetStateAction<CostWeightOverrides>>;
  optObjective: OptObjective;
  setOptObjective: (v: OptObjective) => void;
  optimizing: boolean;
  optResult: OptimizeResult | null;
  appliedKey: string | null;                // `${F}-${S}-${C}` of the design on the Gantt now
  onApply: (opt: OptimizeOption) => void;
  onOptimise: () => void;
  onCancel: () => void;
}

const goalLabel = (k: OptObjective | undefined) =>
  OPT_OBJECTIVES.find(o => o.key === k)?.label ?? String(k ?? "");

const isNum = (x: number | null | undefined): x is number => x != null && isFinite(x);

/** Signed, rounded percentage — "+11 %" (sep " ") or "+11%" (sep ""); "—" if unknown. */
const signedPct = (x: number | null | undefined, sep = " ") => {
  if (!isNum(x)) return "—";
  const r = Math.round(x);
  return `${r < 0 ? "−" : "+"}${Math.abs(r)}${sep}%`;
};

/** Formatted number + unit, or "—" when the value is missing (older backends). */
const withUnit = (x: number | null | undefined, unit: string, d = 0) =>
  isNum(x) ? `${fmt(x, d)}${unit}` : "—";

/** Gantt inputs shown in US units when the display is US (all others are unit-free). */
const UNIT_QTY: Partial<Record<keyof GanttInputs, Quantity>> = {
  mFlow: "capacity", goalT: "tonnes",
  lStation: "length", dStation: "length", tStation: "length",
  rhoProduct: "density",
};

/** min / max attributes for a Gantt input, from lib/limits. */
const bounds = (k: keyof GanttInputs) => inputBounds(GANTT_LIMITS[k]);

const crewsLabel = (c: number) => `${c} crew${c === 1 ? "" : "s"}`;
const designKey = (o: { n_freezers: number; n_stations: number; n_crews: number }) =>
  `${o.n_freezers}-${o.n_stations}-${o.n_crews}`;

/** A typed cost weight that will be ignored (not a finite number ≥ 0). */
const badWeight = (raw: string | undefined) => {
  const t = (raw ?? "").trim();
  if (t === "") return false;
  const v = Number(t);
  return !(isFinite(v) && v >= 0);
};

/**
 * AUTO/MAN field — mirrors the Calculator's SplitOutputRow.
 * AUTO (blue pill): shows the live computed value, read-only.
 * MAN (amber pill): editable override; value is written to `overrideValue`.
 * Mode is an explicit flag that only the pill changes: clearing the MAN input
 * keeps MAN and shows a validation message instead of flipping to AUTO.
 */
function SplitRow({
  label, unit, testId, autoValue, autoDisplay, overrideValue, isManual, onToggle, onChange, decimals = 0, error,
}: {
  label: string;
  unit: string;
  testId: string;
  autoValue: number | null;     // raw computed value (pre-populates MAN)
  autoDisplay: string;          // formatted value shown in AUTO mode
  overrideValue: string;        // the MAN value
  isManual: boolean;            // MAN mode (explicit — not derived from the value)
  onToggle: (on: boolean, prefill: string) => void;
  onChange: (v: string) => void;
  decimals?: number;
  error?: string;               // hard-limit / malformed MAN entry (e.g. "1.2.3")
}) {
  const id = useId();
  const errId = `${id}-err`;

  // Switch to MAN pre-populated with the computed value, rounded to the
  // precision MAN accepts (whole minutes, or 1 dp for defrost).
  const f = 10 ** decimals;
  const togglePill = () => {
    if (isManual) onToggle(false, "");                                                   // back to AUTO
    else onToggle(true, autoValue != null ? String(Math.round(autoValue * f) / f) : ""); // switch to MAN
  };

  return (
    <>
    <div className="fld">
      {isManual
        ? <label className="lbl" htmlFor={id}>{label}</label>
        : <span className="lbl" id={`${id}-lbl`}>{label}</span>}
      <div className={`split${isManual ? " man" : ""}`} data-testid={`${testId}-split`}>
        {isManual ? (
          <input
            id={id}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errId : undefined}
            data-testid={`${testId}-input`}
            className="split-val"
            inputMode="decimal"
            value={overrideValue}
            onChange={(e) => {
              // Digits (plus "." when decimals > 0). A malformed entry such as
              // "1.2.3" is kept so it shows an error — never a silent AUTO.
              const re = decimals > 0 ? /[^0-9.]/g : /[^0-9]/g;
              onChange(e.target.value.replace(re, ""));
            }}
          />
        ) : (
          <div data-testid={`${testId}-output`} className="split-val" aria-labelledby={`${id}-lbl`}>{autoDisplay}</div>
        )}
        <button
          type="button"
          data-testid={`${testId}-pill`}
          className="split-pill"
          onClick={togglePill}
          title={isManual ? "Return to calculated value" : "Override with manual value"}
          aria-label={`${label}: ${isManual ? "manual — return to calculated value" : "automatic — override with manual value"}`}
        >
          {isManual ? "MAN" : "AUTO"}
        </button>
      </div>
      <span className="unit">{unit}</span>
    </div>
    {isManual && error && <div className="fld-err" id={errId}>{error}</div>}
    </>
  );
}

export function LeftRail({
  inputs, set, setValue, setVal, setCheck, blur, notes: siNotes, manual, setManual, clearOverride, errors: siErrors, stale, result,
  autoFreeze, autoDefrost, effFreeze, effDefrost,
  flowMargin, setFlowMargin, freezeMargin, setFreezeMargin, maxCrews, setMaxCrews,
  costWeights, setCostWeights, optObjective, setOptObjective,
  optimizing, optResult, appliedKey, onApply, onOptimise, onCancel,
}: LeftRailProps) {
  const { uiUnits: sys } = useSettings();
  // Display-units view of the SI errors / autocorrect notes (unchanged in SI).
  const errors: Partial<Record<keyof GanttInputs, string>> = {};
  (Object.keys(siErrors) as (keyof GanttInputs)[]).forEach((k) => {
    errors[k] = localiseGanttText(siErrors[k], sys);
  });
  const notes: Partial<Record<keyof GanttInputs, string>> = {};
  (Object.keys(siNotes) as (keyof GanttInputs)[]).forEach((k) => {
    notes[k] = localiseNote(siNotes[k], UNIT_QTY[k], sys);
  });
  /** An SI field shown in display units (unit-converting in US, number input in SI). */
  const unitField = (k: keyof GanttInputs, testId: string, value: string, withBlur: boolean) => (
    <UnitInput
      data-testid={testId} className={cls(k)} type="number" {...bounds(k)}
      quantity={UNIT_QTY[k]} value={value} onValue={setValue(k)} onBlur={withBlur ? blur(k) : undefined}
    />
  );
  const [weightsOpen, setWeightsOpen] = useState(false);
  // Clamp notes for the optimiser fields ("150 → 100 (max)"), until the next edit.
  const [optNotes, setOptNotes] = useState<Record<string, string>>({});
  const clampWithNote = (
    key: string, raw: string, limit: { hard: { min: number; max: number } }, clamped: number,
    setter: (v: string) => void,
  ) => {
    const next = String(clamped);
    setter(next);
    const t = raw.trim();
    const x = Number(t);
    setOptNotes(prev => {
      const out = { ...prev };
      if (t !== "" && t !== next && isFinite(x)) {
        const why = x > limit.hard.max ? "max" : x < limit.hard.min ? "min" : "whole number";
        out[key] = `${t} \u2192 ${next} (${why})`;
      } else delete out[key];
      return out;
    });
  };
  const clearOptNote = (key: string) =>
    setOptNotes(prev => { if (!(key in prev)) return prev; const out = { ...prev }; delete out[key]; return out; });
  // Trade-off picked with a radio but not applied yet; a new search clears it.
  const [pickedKey, setPickedKey] = useState<string | null>(null);
  useEffect(() => { setPickedKey(null); }, [optResult]);
  const r = result?.main;
  const d = r?.derived;

  // Add the error highlight class when a linked field fails validation.
  const cls = (k: keyof GanttInputs) => "box" + (errors[k] ? " err" : "");
  // Linked values with any "!" override stripped — the number inputs can't
  // show the marker, so FieldRow shows a "!" chip instead (see ovr).
  const val = (k: keyof GanttInputs) => stripOverride(String(inputs[k] ?? ""));
  const ovr = (k: keyof GanttInputs) =>
    LINKED_TO_CALC[k] && hasOverride(String(inputs[k] ?? "")) ? () => clearOverride(k) : undefined;

  const tLoMin  = d ? (d.t_load_h * 60).toFixed(1)  : "—";
  const tUnMin  = d ? (d.t_service_h * 60).toFixed(1) : "—";
  const tCycMin = d ? (d.t_cycle_h * 60).toFixed(0)  : "—";
  const mBatch  = d ? massNum(d.m_batch_kg, sys, (x) => fmt(x)) : "—";
  const mStn    = d ? massNum(d.m_station_kg, sys, (x) => fmt(x, 1)) : "—";
  const goalTxt = goalInputText(inputs.goalT, sys);   // the target as typed, in display units
  const nC      = n(inputs.nCrews);
  const modelCode = ganttModelCode(inputs);

  // Unloading overtime (Crews & policy)
  const otH = Math.max(0, n(inputs.overtimeH));
  const showOvertimeUsed = otH > 0 || (r?.overtime_used_h ?? 0) > 0;
  // Unloading beyond the overtime cap: mostly with "Require full cycle" off, but
  // also when crews are still unloading at the end of a shift with no overtime
  const overrunMin = Math.round(Math.max(0, r?.overtime_overrun_h ?? 0) * 60);

  // Auto-optimise — the same clamps GanttPage applies to the request.
  const noTarget   = n(inputs.goalT) <= 0;
  const maxCrewsN  = clampHard(OPT_LIMITS.maxCrews, Math.round(n(maxCrews, 4)));
  const nCustomWts = COST_WEIGHT_FIELDS.filter(
    f => (costWeights[f.key] ?? "").trim() !== "" && !badWeight(costWeights[f.key]),
  ).length;

  const optSummary = optResult
    ? `${optResult.n_freezers}× ${optResult.n_stations} stn · ${crewsLabel(optResult.n_crews)}`
      + (isNum(optResult.headroom_pct) ? ` · ${signedPct(optResult.headroom_pct, "")} margin` : "")
    : `Target ${goalTxt} ${goalUnit(sys)}`;
  const showOptOvertime = !!optResult && ((optResult.overtime_used_h ?? 0) > 0 || otH > 0);

  // Trade-off list: cost as an index where the cheapest option = 100. Omitted
  // while cost_score is null (cost goal disabled) or the minimum isn't > 0.
  const optOptions: OptimizeOption[] = optResult?.options ?? [];
  const costs = optOptions.map(o => o.cost_score).filter(isNum);
  const minCost = costs.length === optOptions.length && costs.length > 0 ? Math.min(...costs) : null;
  const costIndex = (c: number | null) =>
    minCost != null && minCost > 0 && isNum(c) ? Math.round((c / minCost) * 100) : null;

  // Selected trade-off = the radio the user picked, else the design on the
  // Gantt, else the goal's winner. The result box shows whichever is selected.
  const optKeys = optOptions.map(designKey);
  const winnerKey = optKeys[optOptions.findIndex(o => o.is_winner)] ?? optKeys[0];
  const selKey = (pickedKey && optKeys.includes(pickedKey)) ? pickedKey
    : (appliedKey && optKeys.includes(appliedKey)) ? appliedKey : winnerKey;
  const selOpt: OptimizeOption | undefined = optOptions[optKeys.indexOf(selKey)];
  const selApplied = !!selKey && selKey === appliedKey;
  const altOpt = selOpt && !selOpt.is_winner ? selOpt : null;   // a non-winner is selected
  const shown = altOpt ?? optResult;                             // what the result box reports
  const shownStnTotal = shown ? (shown.n_stations_total ?? shown.n_freezers * shown.n_stations) : 0;
  const shownMsg = altOpt && optResult
    ? `Alternative: ${altOpt.n_freezers} freezers × ${altOpt.n_stations} stations, `
      + `${crewsLabel(altOpt.n_crews)} @ ${fmtFlow(altOpt.m_flow_kg_h, sys, (x) => fmt(x))} meets `
      + `${sys === "US" ? tNum1(optResult.target_t, sys) : fmt(optResult.target_t, 1)} ${tUnit(sys)}`
      + (isNum(altOpt.headroom_pct) ? `; tolerates ${signedPct(altOpt.headroom_pct, "")} freeze time.` : ".")
    // Backend sentence: its "N t" / "N kg/h" / "N t/h" figures are converted in US.
    : localiseGanttText(optResult?.message ?? "", sys);

  return (
    <div className={`railL${optimizing ? " is-busy" : ""}`}>

      {optimizing && (
        <div className="opt-overlay" data-testid="gantt-opt-overlay">
          <div className="opt-overlay-card">
            <div className="opt-spinner" />
            <div className="opt-bar"><span /></div>
            <div className="opt-overlay-title">Optimising plant…</div>
            <div className="opt-overlay-sub">
              Searching freezers, stations &amp; crews to hit {goalTxt} {goalUnit(sys)}
            </div>
            <button
              type="button"
              data-testid="gantt-opt-cancel"
              className="opt-cancel"
              onClick={onCancel}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Supply & shift */}
      <Card
        title="Supply & shift"
        summary={`${inputs.mFlow.trim() === "" ? "—" : flowNum(Number(inputs.mFlow), sys, (x) => fmt(x))} ${flowUnit(sys)} · ${val("tShift")} h · ${inputs.startStockBatches} batches start`}
      >
        <FieldRow label="Raw product flow" unit={flowUnit(sys)} error={errors.mFlow}>
          {unitField("mFlow", "gantt-mFlow", inputs.mFlow, false)}
        </FieldRow>
        <FieldRow label="Shift length" unit="h" error={errors.tShift} override={ovr("tShift")} note={notes.tShift}>
          <input data-testid="gantt-tShift" className={cls("tShift")} type="number" {...bounds("tShift")} value={val("tShift")} onChange={set("tShift")} onBlur={blur("tShift")} />
        </FieldRow>
        <FieldRow label="Start stock" unit="batches" error={errors.startStockBatches}>
          <input data-testid="gantt-startStock" className={cls("startStockBatches")} type="number" {...bounds("startStockBatches")} value={inputs.startStockBatches} onChange={set("startStockBatches")} />
        </FieldRow>
        <FieldRow label="Target" unit={goalUnit(sys)} error={errors.goalT}>
          {unitField("goalT", "gantt-goalT", inputs.goalT, false)}
        </FieldRow>
        <p className="note">
          Start stock = pre-staged batches at t=0. 0 = cold start (wait for first batch to accrue).
        </p>
      </Card>

      {/* Freezer sizing — linked */}
      <Card
        title={<>Freezer sizing <span className="tag">⇄ Linked</span></>}
        summary={`${val("nFreezers")} freezers · ${val("nStations")} stn · ${lenText(val("lStation"), sys)}×${lenText(val("dStation"), sys)} ${lenUnit(sys)}`}
      >
        <FieldRow label="No. of freezers" unit="-" error={errors.nFreezers} override={ovr("nFreezers")} note={notes.nFreezers}>
          <input data-testid="gantt-nFreezers" className={cls("nFreezers")} type="number" {...bounds("nFreezers")} value={val("nFreezers")} onChange={set("nFreezers")} onBlur={blur("nFreezers")} />
        </FieldRow>
        <FieldRow label="No. of stations" unit="-" error={errors.nStations} override={ovr("nStations")} note={notes.nStations}>
          <input data-testid="gantt-nStations" className={cls("nStations")} type="number" {...bounds("nStations")} value={val("nStations")} onChange={set("nStations")} onBlur={blur("nStations")} />
        </FieldRow>
        <FieldRow label="Station length" unit={lenUnit(sys)} error={errors.lStation} override={ovr("lStation")} note={notes.lStation}>
          {unitField("lStation", "gantt-lStation", val("lStation"), true)}
        </FieldRow>
        <FieldRow label="Station depth" unit={lenUnit(sys)} error={errors.dStation} override={ovr("dStation")} note={notes.dStation}>
          {unitField("dStation", "gantt-dStation", val("dStation"), true)}
        </FieldRow>
        <FieldRow label="Station thickness" unit={lenUnit(sys)} error={errors.tStation} override={ovr("tStation")} note={notes.tStation}>
          {unitField("tStation", "gantt-tStation", val("tStation"), true)}
        </FieldRow>
        <FieldRow label="Dividers" unit="-" error={errors.nDividers} override={ovr("nDividers")} note={notes.nDividers}>
          <input data-testid="gantt-nDividers" className={cls("nDividers")} type="number" {...bounds("nDividers")} value={val("nDividers")} onChange={set("nDividers")} onBlur={blur("nDividers")} />
        </FieldRow>
        <FieldRow label="Product density" unit={densUnit(sys)} error={errors.rhoProduct} override={ovr("rhoProduct")} note={notes.rhoProduct}>
          {unitField("rhoProduct", "gantt-rhoProduct", val("rhoProduct"), true)}
        </FieldRow>
        <p className="note" style={{ marginTop: 6 }}>Auto-synced from Calculator on every visit.</p>
      </Card>

      {/* Product load computed */}
      <Card
        title={<>Product load <span className="tag">Computed</span></>}
        summary={`${mStn} ${massUnit(sys)}/stn · ${mBatch} ${massUnit(sys)}/batch`}
        defaultOpen={false}
      >
        <FieldRow label="Weight per station" unit={massUnit(sys)}>
          <div data-testid="gantt-mStation" className="box out">{mStn}</div>
        </FieldRow>
        <FieldRow label="Weight per batch" unit={massUnit(sys)}>
          <div data-testid="gantt-mBatch" className="box out">{mBatch}</div>
        </FieldRow>
      </Card>

      {/* Freezing & cycle times — linked */}
      <Card
        title={<>Freezing &amp; cycle times <span className="tag">⇄ Linked</span></>}
        summary={`${effFreeze != null ? Math.round(effFreeze) : "—"} min freeze · ${tCycMin} min cycle`}
        defaultOpen={false}
      >
        <FieldRow label="Time to close / lower" unit="sec" error={errors.closingSec}>
          <div data-testid="gantt-closingSec" className="box out" title="From the Calculator result">{inputs.closingSec || "—"}</div>
        </FieldRow>
        <FieldRow label="Loading time" unit="sec/stn" error={errors.loadingSec} override={ovr("loadingSec")} note={notes.loadingSec}>
          <input data-testid="gantt-loadingSec" className={cls("loadingSec")} type="number" {...bounds("loadingSec")} value={val("loadingSec")} onChange={set("loadingSec")} onBlur={blur("loadingSec")} />
        </FieldRow>
        <SplitRow
          label="Est. freezing time" unit="min" testId="gantt-freeze"
          autoValue={autoFreeze}
          autoDisplay={autoFreeze != null ? String(autoFreeze) : "—"}
          overrideValue={inputs.freezeOverride}
          isManual={manual.freezeOverride}
          onToggle={(on, prefill) => setManual("freezeOverride", on, prefill)}
          onChange={setVal("freezeOverride")}
          error={errors.freezeOverride}
        />
        <SplitRow
          label="Defrosting" unit="min" testId="gantt-defrost"
          autoValue={autoDefrost}
          autoDisplay={autoDefrost != null ? fmt(autoDefrost, 1) : "—"}
          overrideValue={inputs.defrostOverride}
          isManual={manual.defrostOverride}
          onToggle={(on, prefill) => setManual("defrostOverride", on, prefill)}
          onChange={setVal("defrostOverride")}
          decimals={1}
          error={errors.defrostOverride}
        />
        <FieldRow label="Time to open / raise" unit="sec" error={errors.openingSec}>
          <div data-testid="gantt-openingSec" className="box out" title="From the Calculator result">{inputs.openingSec || "—"}</div>
        </FieldRow>
        <FieldRow label="Unloading time" unit="sec/stn" error={errors.unloadingSec} override={ovr("unloadingSec")} note={notes.unloadingSec}>
          <input data-testid="gantt-unloadingSec" className={cls("unloadingSec")} type="number" {...bounds("unloadingSec")} value={val("unloadingSec")} onChange={set("unloadingSec")} onBlur={blur("unloadingSec")} />
        </FieldRow>
        <FieldRow label="Wait / misc" unit="min" error={errors.waitingMin} override={ovr("waitingMin")} note={notes.waitingMin}>
          <input data-testid="gantt-waitingMin" className={cls("waitingMin")} type="number" {...bounds("waitingMin")} step="0.5" value={val("waitingMin")} onChange={set("waitingMin")} onBlur={blur("waitingMin")} />
        </FieldRow>
        <div className="subtotbar">
          <span className="lbl">Total loading time</span>
          <div data-testid="gantt-total-loading" className="box out" style={{ flex: "0 0 120px", height: 32 }}>{tLoMin}</div>
          <span className="unit">min</span>
        </div>
        <div className="subtotbar">
          <span className="lbl">Total unloading time</span>
          <div data-testid="gantt-total-unloading" className="box out" style={{ flex: "0 0 120px", height: 32 }}>{tUnMin}</div>
          <span className="unit">min</span>
        </div>
        <div className="totbar">
          <span className="lbl">Total cycle time</span>
          <div className="box out" style={{ flex: "0 0 120px", height: 32, background: "rgba(255,255,255,.12)", borderColor: "rgba(255,255,255,.2)", color: "#fff" }} data-testid="gantt-total-cycle">
            {tCycMin}
          </div>
          <span className="unit" style={{ color: "#c4cdd8" }}>min</span>
        </div>
      </Card>

      {/* Crews & policy */}
      <Card
        title="Crews & policy"
        summary={`${inputs.nCrews} crew${nC === 1 ? "" : "s"}${otH > 0 ? ` · ${otH} h unload OT` : ""}`}
      >
        <FieldRow label="Crews / Teams" unit="-" error={errors.nCrews}
          note={nC > Math.max(1, Math.round(n(inputs.nFreezers))) ? "Crews limited to the number of freezers" : undefined}>
          <input data-testid="gantt-nCrews" className={cls("nCrews")} type="number" {...bounds("nCrews")} step="1" value={inputs.nCrews} onChange={set("nCrews")} />
        </FieldRow>
        <FieldRow label="Unloading Overtime" unit="h" error={errors.overtimeH}>
          <input data-testid="gantt-overtimeH" className={cls("overtimeH")} type="number" {...bounds("overtimeH")} step="0.5" value={inputs.overtimeH} onChange={set("overtimeH")} />
        </FieldRow>
        <label className="chk">
          <input data-testid="gantt-requireFullCycle" type="checkbox" checked={inputs.requireFullCycle} onChange={setCheck("requireFullCycle")} />
          <span>Only start a load whose full cycle finishes in the shift (plus unloading overtime)</span>
        </label>
        {result?.rec_crews != null && (
          <p className="note">
            Recommended crews: <strong data-testid="gantt-rec-crews" style={{ color: "var(--gp-blue)" }}>{result.rec_crews}</strong>
          </p>
        )}
        <p className="note">
          Overtime is for unloading only — no loading after the shift ends. 0 = off.
        </p>
      </Card>

      {/* Auto-optimise plant */}
      <Card
        title={<>Auto-optimise plant <span className="tag">Goal Seek</span></>}
        summary={optSummary}
      >
        <div className="opt-obj" data-testid="gantt-obj-selector">
          <span className="opt-obj-lbl">Optimise for...</span>
          <div className="opt-obj-grid">
            {OPT_OBJECTIVES.map((o) => (
              <button
                key={o.key}
                type="button"
                data-testid={`gantt-obj-${o.key}`}
                className={`opt-obj-btn${o.disabled ? " soon" : optObjective === o.key ? " active" : ""}`}
                onClick={() => { if (!o.disabled) setOptObjective(o.key); }}
                disabled={optimizing || !!o.disabled}
                aria-pressed={!o.disabled && optObjective === o.key}
                title={o.hint}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <FieldRow label="Flow headroom" unit="%" note={optNotes.flow}>
          <input
            data-testid="gantt-flow-margin"
            className="box"
            type="number"
            {...inputBounds(OPT_LIMITS.flowMargin)}
            step="5"
            value={flowMargin}
            disabled={optimizing}
            title="Extra raw-product flow on top of the minimum the plant needs (0 % = bare minimum supply)"
            onChange={(e) => { clearOptNote("flow"); setFlowMargin(e.target.value); }}
            onBlur={() => clampWithNote("flow", flowMargin, OPT_LIMITS.flowMargin, clampHard(OPT_LIMITS.flowMargin, n(flowMargin, DEFAULT_FLOW_MARGIN_PCT)), setFlowMargin)}
          />
        </FieldRow>
        <FieldRow label="Safety margin" unit="%" note={optNotes.freeze}>
          <input
            data-testid="gantt-freeze-margin"
            className="box"
            type="number"
            {...inputBounds(OPT_LIMITS.freezeMargin)}
            step="5"
            value={freezeMargin}
            disabled={optimizing}
            title="Plan as if freezing takes this much longer, so the chosen plant still hits target"
            onChange={(e) => { clearOptNote("freeze"); setFreezeMargin(e.target.value); }}
            onBlur={() => clampWithNote("freeze", freezeMargin, OPT_LIMITS.freezeMargin, clampHard(OPT_LIMITS.freezeMargin, n(freezeMargin, DEFAULT_SAFETY_MARGIN_PCT)), setFreezeMargin)}
          />
        </FieldRow>
        <FieldRow label="Max crews" unit="-" note={optNotes.crews}>
          <input
            data-testid="gantt-max-crews"
            className="box"
            type="number"
            {...inputBounds(OPT_LIMITS.maxCrews)}
            step="1"
            value={maxCrews}
            disabled={optimizing}
            title="Largest number of crews the search may use (1–4)"
            onChange={(e) => { clearOptNote("crews"); setMaxCrews(e.target.value); }}
            onBlur={() => clampWithNote("crews", maxCrews, OPT_LIMITS.maxCrews, maxCrewsN, setMaxCrews)}
          />
        </FieldRow>

        {/* Cost weights — hidden until the "Lowest overall cost" goal is enabled */}
        {COST_GOAL_ENABLED && (
          <div className="opt-weights">
            <button
              type="button"
              className="opt-weights-toggle"
              data-testid="gantt-cost-weights-toggle"
              aria-expanded={weightsOpen}
              aria-controls="gantt-cost-weights-panel"
              disabled={optimizing}
              onClick={() => setWeightsOpen(o => !o)}
            >
              <svg className="opt-weights-chev" width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Cost weights
              <span className="opt-weights-sum">{nCustomWts ? `${nCustomWts} custom` : "Defaults"}</span>
            </button>
            {weightsOpen && (
              <div className="opt-weights-panel" id="gantt-cost-weights-panel" data-testid="gantt-cost-weights">
                {COST_WEIGHT_FIELDS.map((f) => {
                  const raw = costWeights[f.key] ?? "";
                  const eff = optResult?.cost_weights?.[f.key];
                  return (
                    <FieldRow key={f.key} label={f.label} unit="stn-eq">
                      <input
                        data-testid={`gantt-cw-${f.key}`}
                        className={"box" + (badWeight(raw) ? " err" : "")}
                        type="number"
                        min="0"
                        step="any"
                        value={raw}
                        placeholder={isNum(eff) ? String(+eff.toFixed(2)) : "default"}
                        title={f.hint}
                        disabled={optimizing}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCostWeights(prev => ({ ...prev, [f.key]: v }));
                        }}
                      />
                    </FieldRow>
                  );
                })}
                <p className="note">1 = one station incl. its refrigeration share. Blank = default.</p>
              </div>
            )}
          </div>
        )}

        <div className="opt-cta-row">
          <span className="opt-cta-lbl">Goal Seek ➔</span>
          <button
            data-testid="gantt-optimise-btn"
            type="button"
            className="opt-cta-btn"
            onClick={onOptimise}
            disabled={optimizing || noTarget}
            title={noTarget ? `Set a target (${goalUnit(sys)}) before optimising.` : "Find the best plant for the selected goal"}
          >
            {optimizing ? "Optimising…" : "Optimise Plant"}
          </button>
        </div>

        {optResult && shown && (
          <div
            data-testid="gantt-opt-result"
            className={`opt-result${optResult.feasible ? " ok" : " warn"}`}
          >
            <div className="opt-msg">
              {optResult.feasible ? "✓" : "⚠"} {shownMsg}
            </div>
            <div className="opt-grid">
              <span>Freezers</span><strong>{shown.n_freezers}</strong>
              <span>Stations</span><strong>{shown.n_stations} / freezer · {fmt(shownStnTotal)} total</strong>
              <span>Crews</span><strong>{shown.n_crews}</strong>
              <span>Raw flow</span><strong>{isNum(shown.m_flow_kg_h) ? fmtFlow(shown.m_flow_kg_h, sys, (x) => fmt(x)) : "—"}</strong>
              <span>Safety margin</span>
              <strong>{isNum(shown.headroom_pct) ? `${signedPct(shown.headroom_pct)} freeze time` : "—"}</strong>
              <span>Max wait</span><strong>{withUnit(shown.wait_max_min, " min")}</strong>
              {showOptOvertime && (
                <><span>Overtime used</span><strong>{withUnit(shown.overtime_used_h, " h", 1)}</strong></>
              )}
              <span>Output</span><strong>{sys === "US"
                ? (isNum(shown.achieved_t) ? `${tNum1(shown.achieved_t, sys)} ${tUnit(sys)}` : "—")
                : withUnit(shown.achieved_t, " t", 1)}</strong>
              <span>Crew util</span><strong>{withUnit(shown.crew_util * 100, "%")}</strong>
            </div>
            {optResult.truncated && (
              <div className="opt-warn" data-testid="gantt-opt-truncated">
                Search stopped at its time limit — this may not be the best design.
              </div>
            )}
          </div>
        )}

        {optOptions.length > 0 && (() => {
          const keys = optKeys;
          return (
            <div className="opt-options" data-testid="gantt-opt-options">
              <div className="opt-options-head">Trade-offs for this target</div>
              <div role="radiogroup" aria-label="Trade-offs for this target">
                {optOptions.map((o, i) => {
                  const key = keys[i];
                  const cost = costIndex(o.cost_score);
                  return (
                    <label
                      key={`${i}-${key}`}
                      className={`opt-option${key === selKey ? " sel" : ""}`}
                      data-testid={`gantt-opt-option-${i}`}
                      title={o.is_winner ? `Chosen for: ${goalLabel(optResult?.objective ?? optObjective)}` : undefined}
                    >
                      <input
                        type="radio"
                        name="gantt-opt-option"
                        value={key}
                        checked={key === selKey}
                        disabled={optimizing}
                        onChange={() => setPickedKey(key)}
                      />
                      <span className="opt-option-txt">
                        <strong>
                          {o.n_freezers} × {o.n_stations} stn · {crewsLabel(o.n_crews)} · {isNum(o.m_flow_kg_h) ? fmtFlow(o.m_flow_kg_h, sys, (x) => fmt(x)) : "—"}
                        </strong>
                        <span className="opt-option-l2">
                          {signedPct(o.headroom_pct)} margin · {withUnit(o.wait_max_min, " min")} wait
                          {cost != null && <> · <span title="Relative plant cost — the cheapest option here = 100">cost {cost}</span></>}
                          {(o.overtime_used_h ?? 0) > 0 && <> · {fmt(o.overtime_used_h, 1)} h OT</>}
                        </span>
                      </span>
                      {key === appliedKey && <span className="opt-on-gantt">On Gantt</span>}
                    </label>
                  );
                })}
              </div>
              <button
                type="button"
                className="opt-apply-sel"
                data-testid="gantt-opt-apply"
                disabled={selApplied || optimizing || !selOpt}
                onClick={() => selOpt && onApply(selOpt)}
                title={selApplied ? "This design is on the Gantt now" : "Put the selected design on the Gantt"}
              >
                {selApplied ? "Selected design is on the Gantt" : "Apply selected design"}
              </button>
            </div>
          );
        })()}

        <p className="note">
          Searches freezers (1–20), stations per freezer (10 up to the plate-stack
          limit) and {maxCrewsN === 1 ? "1 crew" : `crews (1–${maxCrewsN})`} for
          the plant that best meets your goal
          at {goalTxt} {goalUnit(sys)}. Safety margin plans as if freezing takes
          that much longer, so the plant still hits target on a slow day; flow
          headroom adds margin on the raw supply. Unloading overtime set under
          Crews &amp; policy is respected. The trade-offs list shows the best
          alternatives — select one and Apply selected design to put it on the
          Gantt. All other inputs stay fixed.
        </p>
        <p className="note" data-testid="gantt-opt-overtime-help">
          Recommendations always finish unloading within overtime.
        </p>
        <p className="note" data-testid="gantt-flow-dip-help">
          Throughput can occasionally dip slightly when flow rises (scheduling
          effect); the goal-seek allows for this.
        </p>
      </Card>

      {/* Results */}
      <Card
        title={<>Results <span className="tag">Live</span></>}
        summary={r ? `${r.batches} batches · ${fmtKgAuto(r.mass_kg, sys)}` : "—"}
      >
        <FieldRow label="Batches / shift" unit="-">
          <div data-testid="gantt-batches" className="box out">{r ? fmt(r.batches) : "—"}</div>
        </FieldRow>
        <FieldRow label="Frozen / shift" unit="">
          <div data-testid="gantt-mass" className="box out">{r ? fmtKgAuto(r.mass_kg, sys) : "—"}</div>
        </FieldRow>
        <FieldRow label="Frozen / week (5)" unit="">
          <div data-testid="gantt-mass-week" className="box out">{r ? fmtKgAuto(r.mass_kg * 5, sys) : "—"}</div>
        </FieldRow>
        <FieldRow label="Crew utilisation" unit="%">
          <div data-testid="gantt-crew-util" className="box out">{r ? fmt(r.crew_util * 100, 0) : "—"}</div>
        </FieldRow>
        <FieldRow label="First load at" unit="min">
          <div data-testid="gantt-first-load" className="box out">{r ? fmt(r.first_load_min, 0) : "—"}</div>
        </FieldRow>
        <FieldRow label="Product max. wait" unit="min">
          <div data-testid="gantt-wait-max" className="box out">{fmt(r?.wait_max_min, 0)}</div>
        </FieldRow>
        {showOvertimeUsed && (
          <FieldRow label="Overtime Used" unit="h">
            <div data-testid="gantt-overtime-used" className="box out">{fmt(r?.overtime_used_h, 1)}</div>
          </FieldRow>
        )}
        {overrunMin > 0 && (
          <div className="opt-warn" role="status" data-testid="gantt-overtime-overrun">
            Unloading runs {overrunMin} min past the overtime cap
            {inputs.requireFullCycle ? "." : <> (&lsquo;Require full cycle&rsquo; is off).</>}
          </div>
        )}
      </Card>

      {/* Export Report — shared card (identical to the Calculator's) */}
      <ExportReportCard modelCode={modelCode} nFreezers={inputs.nFreezers} ready={!!result} current={!stale} page="gantt" />

    </div>
  );
}
